import type { PostHogConfig } from "posthog-js";
import posthog from "posthog-js";

import { POSTHOG_EU_HOST, scrubEvent } from "@gately/observability";

import { env } from "~/env";

export function posthogKey(): string | undefined {
  return env.VITE_POSTHOG_KEY;
}

/** The shared posthog-js instance, passed to <PostHogProvider client={...}>. */
export const posthogClient = posthog;

/** Whether `initWebPostHog` actually ran `posthog.init` (a key, and not a
 *  kiosk route): identify/reset are no-ops otherwise. */
let initialised = false;

/**
 * Initialize PostHog eagerly at app bootstrap (before React renders), so the
 * singleton is always ready before any component effect calls identify().
 * Relying on <PostHogProvider apiKey> instead defers init to a useEffect, which
 * races child identify() effects on a direct load of a logged-in route and
 * produces "event submitted without an api_key". No-ops without a key, and on the
 * public kiosk routes (see `posthogAllowedOnPath`).
 */
export function initWebPostHog(): void {
  const key = posthogKey();
  if (!key) return;
  if (!posthogAllowedOnPath(window.location.pathname)) return;
  posthog.init(key, posthogOptions());
  initialised = true;
}

/**
 * The public kiosk — the borne tablet (`/borne…`) and, from slice 03, the
 * phone form (`/b/<token>`) — is never tracked: autocapture and session
 * replay would see visitors' names, phones and ID numbers typed at the
 * reception. PostHog is simply not initialised when the app boots there: a
 * kiosk never navigates out of its tree, and a boot on any other route is
 * tracked as before.
 */
export function posthogAllowedOnPath(pathname: string): boolean {
  return !/^\/(borne(\/|$)|b\/)/.test(pathname);
}

// Redact the /pass/<token> capability token from any URL-bearing property, then
// strip phone / synthetic-email / bearer PII from every string property (e.g. a
// phone number captured in an autocapture $el_text). Runs on every event.
export function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  for (const key of ["$current_url", "$referrer", "$pathname"]) {
    const value = properties[key];
    if (typeof value === "string") {
      properties[key] = value.replace(/\/pass\/[^/?#]+/g, "/pass/[redacted]");
    }
  }
  // PostHog injects the project API key as `properties.token` (that is where the
  // ingestion API reads it from). scrubEvent's SENSITIVE_KEY matches "token" and
  // would delete it, so every event would be rejected with 401 "event submitted
  // without an api_key" and nothing would be recorded. Preserve it across the scrub.
  const posthogToken = properties.token;
  const scrubbed = scrubEvent(properties);
  if (posthogToken !== undefined) scrubbed.token = posthogToken;
  return scrubbed;
}

export function posthogOptions(): Partial<PostHogConfig> {
  return {
    api_host: env.VITE_POSTHOG_HOST ?? POSTHOG_EU_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: true,
    sanitize_properties: sanitizeProperties,
    session_recording: {
      // OTP screen safety: never record input contents.
      maskAllInputs: true,
    },
  };
}

export function identifyPostHog(
  id: string,
  properties?: Record<string, string | number | boolean | null>,
): void {
  if (!initialised) return;
  posthog.identify(id, properties);
}

export function resetPostHog(): void {
  if (!initialised) return;
  posthog.reset();
}
